import { ExportOutlined } from "@ant-design/icons";
import { Auth0SecretVault } from "@p0security/integrations-auth0-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-auth0-shared/hosting";
import { AUTH0_MANAGEMENT_SCOPES } from "@p0security/integrations-auth0-shared/scopes";
import type {
  Auth0ConnectorHosting,
  Auth0TenantConfig,
} from "@p0security/integrations-auth0-shared/types";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  CommandDisplay,
  Flex,
  Steps,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import type { ComponentProps, ReactNode } from "react";

import { PLACEHOLDER_DOMAIN, toEmailDomain } from "./connector-iac.ts";
import type { ReadGrantBlock } from "./tenant-iac.ts";

const { Paragraph, Text } = Typography;

type Vault = Auth0TenantConfig["vault"];

/** What a tenant's page can show of its read grant. */
export type ReadGrant =
  | {
      status: "ready";
      /** The Terraform that lets the connector read the client secret, in
       * the order to apply it */
      blocks: ReadGrantBlock[];
      hosting: Auth0ConnectorHosting;
      /** The AWS account that holds the secret, when it isn't the
       * connector's */
      vaultAccountId: string | undefined;
    }
  /** The tenant's connector isn't installed */
  | { status: "no-connector" }
  /** The secret isn't in the connector's cloud, so no Terraform grants it */
  | { status: "vault-outside-cloud"; hosting: Auth0ConnectorHosting };

type Step = { content: ReactNode; status?: "error" | "finish"; title: string };

const AUTH0_DASHBOARD_URL = "https://manage.auth0.com/";

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

// Reads the client secret without echoing it or leaving it in the shell's
// history. Each command below takes it from standard input, so it's never an
// argument, which other users of the machine could list.
const READ_SECRET = "read -rs AUTH0_CLIENT_SECRET";

const CLEAR_SECRET = "unset AUTH0_CLIENT_SECRET";

const pipeSecret = (...lines: string[]) =>
  `printf %s "$AUTH0_CLIENT_SECRET" | ${shellLines(...lines)}`;

const toVaultLabel = (vault: Vault) =>
  Auth0SecretVault.options[vault.type].label;

/** How to store the client secret as the tenant's secret: where the secret
 * is, such as "account 123456789012 and region us-west-2", the command that
 * creates it, and the command for a secret that already exists. */
const toStoreInstructions = (
  { clientSecretName: name, vault }: Auth0TenantConfig,
  { crossAccount }: { crossAccount: boolean }
): { create: string; location: string; update: string } => {
  switch (vault.type) {
    case "aws-sm":
      return {
        create: pipeSecret(
          "aws secretsmanager create-secret",
          `--name ${name}`,
          ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
        location: `account ${vault.install} and region ${vault.secretsRegion}`,
        update: pipeSecret(
          "aws secretsmanager put-secret-value",
          `--secret-id ${name}`,
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
      };
    case "gcp-sm":
      return {
        create: pipeSecret(
          `gcloud secrets create ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
        location: `project ${vault.install}`,
        update: pipeSecret(
          `gcloud secrets versions add ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
      };
    default:
      throw assertNever(vault);
  }
};

/** Whether the connector's allowed-domain `pattern` lets it act for
 * `domain`, as the connector tests it: unanchored, against the part of an
 * email after the "@". A pattern that doesn't compile allows nothing. */
export const allowsDomain = ({
  domain,
  pattern,
}: {
  domain: string;
  pattern: string;
}) => {
  try {
    return new RegExp(pattern.trim()).test(domain);
  } catch {
    return false;
  }
};

/** Says how to create the machine-to-machine application the connector acts
 * as, and which Management API scopes to authorize it for. The new tenant's
 * form asks for its client ID, so the form shows this too. */
export const ApplicationInstructions: React.FC<{ tenant?: string }> = ({
  tenant,
}) => (
  <>
    <Text>
      In the Auth0 Dashboard{tenant === undefined ? "" : ` for ${tenant}`}, go
      to Applications, then Applications, and create a Machine to Machine
      application. Authorize it for the Auth0 Management API with exactly these
      scopes, and no others:
    </Text>
    <CommandDisplay commands={AUTH0_MANAGEMENT_SCOPES.join("\n")} />
  </>
);

const CreateApplication: React.FC<{ clientId: string; tenant: string }> = ({
  clientId,
  tenant,
}) => (
  <Flex gap={8} vertical>
    <ApplicationInstructions tenant={tenant} />
    <Text>
      The connector reads and changes users&apos; roles, their permissions and
      organization memberships, and nothing else. Its client ID must be the one
      you entered, <Text code>{clientId}</Text>. Copy its client secret from the
      application&apos;s settings.
    </Text>
    <div>
      <Button
        href={AUTH0_DASHBOARD_URL}
        icon={<ExportOutlined />}
        rel="noopener noreferrer"
        target="_blank"
      >
        Open the Auth0 Dashboard
      </Button>
    </div>
  </Flex>
);

const StoreSecret: React.FC<{
  crossAccount: boolean;
  item: Auth0TenantConfig;
}> = ({ crossAccount, item }) => {
  const { clientSecretName: name, vault } = item;
  const { create, location, update } = toStoreInstructions(item, {
    crossAccount,
  });
  return (
    <Flex gap={8} vertical>
      <Text>
        Store the client secret in {toVaultLabel(vault)} as the secret{" "}
        <Text code>{name}</Text>, in {location}.{" "}
        {crossAccount && (
          <>
            The connector runs in another AWS account, so the secret needs a
            customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
            with that key&apos;s ARN.{" "}
          </>
        )}
        Run this first, paste the client secret and press Enter. It reads the
        secret without showing it:
      </Text>
      <CommandDisplay commands={READ_SECRET} />
      <Text>Then create the secret:</Text>
      <CommandDisplay commands={create} />
      <Text>
        If the secret already exists, store the client secret as a new version
        instead:
      </Text>
      <CommandDisplay commands={update} />
      <Text>Then clear the client secret from your shell:</Text>
      <CommandDisplay commands={CLEAR_SECRET} />
      <Text>
        P0 never reads the client secret. Only your connector does. To rotate it
        later, store the new one the same way, as a new version of this secret.
      </Text>
    </Flex>
  );
};

const toDeployedStep = (
  connectorId: string,
  hosting: Auth0ConnectorHosting
): Omit<Step, "title"> => ({
  content: (
    <Text>
      Done: the connector {connectorId} runs as {renderHosting(hosting)}. It
      acts only for the email domains its deployment allows.
    </Text>
  ),
  status: "finish",
});

/** Says where to apply the Terraform that lets the connector read the client
 * secret. */
const GrantRead: React.FC<{
  /** The AWS account that holds the secret, when it isn't the connector's */
  vaultAccountId: string | undefined;
}> = ({ vaultAccountId }) => (
  <Text>
    Apply the Terraform below. It lets the connector read this one secret, and
    no other.
    {vaultAccountId && (
      <>
        {" "}
        The secret and the connector are in different AWS accounts, so there are
        two configurations: one for the connector&apos;s account, and one for
        account {vaultAccountId}, which holds the secret.
      </>
    )}
  </Text>
);

/** What the steps that depend on the tenant's connector show, for each state
 * of its read grant: the connector's own step, what the step that grants the
 * read says, and whether the secret is in another AWS account than the
 * connector. */
const toConnectorSteps = (
  connectorId: string,
  grant: ReadGrant
): { crossAccount: boolean; deploy: Omit<Step, "title">; read: ReactNode } => {
  switch (grant.status) {
    case "no-connector":
      return {
        crossAccount: false,
        deploy: {
          content: (
            <Alert
              message={`The connector ${connectorId} isn't installed. Install it again under Connector, with the same ID and hosting, or remove this tenant and add it again with another connector.`}
              showIcon
              type="error"
            />
          ),
          status: "error",
        },
        read: <Text>Install the connector first.</Text>,
      };
    case "ready":
      return {
        crossAccount: grant.vaultAccountId !== undefined,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: <GrantRead vaultAccountId={grant.vaultAccountId} />,
      };
    case "vault-outside-cloud":
      return {
        crossAccount: false,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: (
          <Alert
            message={`${VAULT_OUTSIDE_CONNECTOR_CLOUD} Remove this tenant and add it again.`}
            showIcon
            type="error"
          />
        ),
      };
    default:
      throw assertNever(grant);
  }
};

/** Says what "Next" checks, and that "Finish" on the next page completes the
 * install. Once the connector has reported its allowed domains, it warns when
 * they leave out the admin's own. */
const Validate: React.FC<{
  adminEmail: string | null | undefined;
  allowedDomains: string | undefined;
  tenant: string;
}> = ({ adminEmail, allowedDomains, tenant }) => {
  const domain = toEmailDomain(adminEmail);
  return (
    <Flex gap={8} vertical>
      <Text>
        Click &quot;Next&quot;. P0 asks the connector to read the client secret,
        sign in to {tenant} with the client ID and secret, and check that the
        application has every scope above. If a check fails, P0 says which one.
        If the connector is denied the secret right after you apply the
        Terraform, wait a few seconds and click &quot;Next&quot; again.
      </Text>
      <Text>
        When the checks pass, click &quot;Finish&quot; on the next page. Access
        in {tenant} can&apos;t be requested until you do.
      </Text>
      {allowedDomains &&
        domain &&
        !allowsDomain({ domain, pattern: allowedDomains }) && (
          <Alert
            message={`The connector allows the email domains ${allowedDomains}, which leaves out your own, ${domain}. If people at ${domain} should be able to request access in ${tenant}, change domain_allow_pattern in the connector's Terraform and apply it.`}
            showIcon
            type="warning"
          />
        )}
    </Flex>
  );
};

/**
 * The steps that install one Auth0 tenant, in the order the customer takes
 * them, shown above the tenant's Terraform.
 *
 * The customer deploys the connector before adding the tenant, since the
 * tenant's form lists only installed connectors, so that step shows as done,
 * or as failed if someone has since removed the connector.
 */
export const TenantSteps: React.FC<{
  /** The signed-in admin's email. The steps warn when the allowed domains
   * leave out its domain. */
  adminEmail: string | null | undefined;
  grant: ReadGrant;
  item: Auth0TenantConfig;
  /** The tenant's canonical Auth0 domain */
  tenant: string;
}> = ({ adminEmail, grant, item, tenant }) => {
  const { crossAccount, deploy, read } = toConnectorSteps(
    item.connector,
    grant
  );
  const steps: Step[] = [
    {
      content: <CreateApplication clientId={item.clientId} tenant={tenant} />,
      title: "Create the Management API application",
    },
    {
      content: <StoreSecret crossAccount={crossAccount} item={item} />,
      title: `Store the client secret in ${toVaultLabel(item.vault)}`,
    },
    { ...deploy, title: "Deploy the connector" },
    { content: read, title: "Let the connector read the client secret" },
    {
      content: (
        <Validate
          adminEmail={adminEmail}
          allowedDomains={item.allowedDomains}
          tenant={tenant}
        />
      ),
      title: "Validate",
    },
  ];
  const items: ComponentProps<typeof Steps>["items"] = steps.map(
    ({ content, status, title }) => ({
      description: content,
      status: status ?? "process",
      title,
    })
  );
  return <Steps direction="vertical" items={items} />;
};

/** What to do with a connector's Terraform, shown above it. */
export const ConnectorHelp: React.FC<{
  /** The signed-in admin's email, whose domain the Terraform's allowed-domain
   * pattern starts as */
  adminEmail: string | null | undefined;
  connectorId: string;
  hosting: Auth0ConnectorHosting;
}> = ({ adminEmail, connectorId, hosting }) => (
  <>
    <Paragraph>
      The Terraform below deploys the connector {connectorId} as{" "}
      {renderHosting(hosting)}, and lets P0 invoke it. The connector reaches
      your Auth0 tenants over the internet, so it needs no VPC.
    </Paragraph>
    <Paragraph>
      Before you apply it, set <Text code>domain_allow_pattern</Text> to the
      email domains the connector may act for.{" "}
      {toEmailDomain(adminEmail)
        ? "P0 fills in your own email's domain as a start."
        : `P0 doesn't know your email's domain, so it fills in ${PLACEHOLDER_DOMAIN}, which matches nobody: replace it.`}{" "}
      The pattern lives only in your deployment: P0 can&apos;t change it, and
      shows the one deployed on each tenant as &quot;Allowed domains&quot;.
    </Paragraph>
    <Paragraph>
      Then click &quot;Next&quot;, and P0 checks that it can invoke the
      connector. When it can, click &quot;Finish&quot; on the next page: tenants
      can&apos;t name the connector until you do. Each tenant&apos;s own
      Terraform then lets the connector read that tenant&apos;s client secret.
    </Paragraph>
  </>
);
